/**
 * Mel — Altera Performance Advisor chat widget
 *
 * Lives in the storefront. Sends messages to the Mel Cloudflare Worker
 * (URL set via data-mel-worker attribute on each root element).
 *
 * Supports two render modes via data-mel-mode:
 *   - "floating" (default) — bottom-right bubble + collapsible panel
 *   - "inline"             — panel is always open, no bubble (used on /pages/contact)
 *
 * Conversation history is module-shared and persisted to sessionStorage,
 * so navigating between pages (or between the floating widget and the
 * inline panel) preserves context.
 *
 * No external dependencies. Vanilla JS only.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'altera-mel-conversation-v1';
  let messages = loadConversation();

  const roots = document.querySelectorAll('.altera-mel');
  if (roots.length === 0) return;
  roots.forEach(initInstance);

  function initInstance(root) {
    const WORKER_URL = root.dataset.melWorker;
    if (!WORKER_URL) {
      console.warn('[mel] Missing data-mel-worker attribute. Widget disabled.');
      return;
    }

    const CUSTOMER_ID = root.dataset.melCustomerId || null;
    const CUSTOMER_NAME = root.dataset.melCustomerName || null;
    const CUSTOMER_EMAIL = root.dataset.melCustomerEmail || null;
    const CUSTOMER_ORDERS_COUNT = parseInt(root.dataset.melCustomerOrdersCount || '0', 10) || 0;
    const CUSTOMER_TAGS = (root.dataset.melCustomerTags || '')
      .split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
    const IS_WHOLESALE = CUSTOMER_TAGS.includes('activewholesale');
    const GREETING = root.dataset.melGreeting || 'Hi, I\'m Mel.';
    const WHOLESALE_GREETING = "Welcome! I'm Mel, Altera's AI Performance Advisor. I can help you plan your store's assortment, check availability, replenish products, and answer questions for your sales team.";
    const IS_INLINE = root.dataset.melMode === 'inline';

    // Starter-prompt chips shown once, right after the greeting, before the
    // customer sends anything. Segment logic:
    //   NEW       = not logged in (no CUSTOMER_ID)
    //   RETURNING = logged in AND has placed ≥1 order
    //   ACCOUNT   = logged in AND has 0 orders (falls into the NEW set —
    //               they still need the browse / find-first-pair paths)
    const STARTER_PROMPTS_NEW = [
      'Help me choose socks',
      'What makes Altera different?',
      'Compare sock styles',
      'How does the warranty work?',
    ];
    const STARTER_PROMPTS_RETURNING = [
      'Find a pair I ordered before',
      'Help me choose something new',
      'Check my Paca Points',
      'Leave a Review',
      'Help with my order or socks',
    ];
    const STARTER_PROMPTS_WHOLESALE = [
      'Help me build an order',
      'Reorder from past purchases',
      'Check stock and availability',
      'Help me sell Altera',
      'Help with my wholesale account or order',
    ];
    function starterPromptsForCustomer() {
      if (IS_WHOLESALE) return STARTER_PROMPTS_WHOLESALE;
      const isReturning = CUSTOMER_ID && CUSTOMER_ORDERS_COUNT >= 1;
      return isReturning ? STARTER_PROMPTS_RETURNING : STARTER_PROMPTS_NEW;
    }

    // Per-tab session ID — kept in sessionStorage so refreshes stay in the same
    // conversation thread but a new tab/window starts a new one.
    function getMelSessionId() {
      try {
        var key = 'altera-mel-session-id';
        var sid = sessionStorage.getItem(key);
        if (!sid) {
          sid = 'mel-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
          sessionStorage.setItem(key, sid);
        }
        return sid;
      } catch (e) {
        return 'mel-anon-' + Math.random().toString(36).slice(2, 10);
      }
    }
    const SESSION_ID = getMelSessionId();

    const bubble = root.querySelector('[data-mel-toggle]');
    const panel = root.querySelector('[data-mel-panel]');
    const closeBtn = root.querySelector('[data-mel-close]');
    const messagesEl = root.querySelector('[data-mel-messages]');
    const typingEl = root.querySelector('[data-mel-typing]');
    const form = root.querySelector('[data-mel-form]');
    const input = root.querySelector('[data-mel-input]');
    const sendBtn = root.querySelector('[data-mel-send]');
    const attachBtn = root.querySelector('[data-mel-attach]');
    const fileInput = root.querySelector('[data-mel-file]');
    const attachStrip = root.querySelector('[data-mel-attachments]');
    const dropzone = root.querySelector('[data-mel-dropzone]');

    // In-flight and queued attachments for the NEXT message the customer sends.
    // Each entry: { id, name, mime, size, thumbUrl (blob:), status ('uploading'|'ready'|'error'), url? (CDN), error? }
    const MAX_ATTACHMENTS = 4;
    const MAX_FILE_BYTES = 5 * 1024 * 1024;
    const ALLOWED_MIME = /^image\/(jpeg|png|webp)$/i;
    let attachments = [];
    let attachSeq = 0;

    let inFlight = false;

    // ---- init ----
    root.hidden = false;
    renderAllMessages();

    if (IS_INLINE) {
      // Panel is always open. Show greeting immediately if the user hasn't
      // chatted yet in this session.
      panel.hidden = false;
      if (messages.length === 0) {
        appendMessage('assistant', greetingText(), { persist: true });
        renderStarterChips();
      }
      scrollToBottom();
    } else {
      bubble.addEventListener('click', () => openPanel(true));
      closeBtn.addEventListener('click', closePanel);
      document.addEventListener('keydown', onKeyDown);

      // Auto-open when the URL carries ?mel=open or ?chat=open. Lets us drop a
      // direct-to-chat link in Google Business Profile, email signatures, etc.
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('mel') === 'open' || params.get('chat') === 'open') {
          setTimeout(() => openPanel(true), 150);
          // Strip the param from the visible URL so back-button / bookmarks
          // don't re-trigger the auto-open weirdly.
          params.delete('mel');
          params.delete('chat');
          const clean = window.location.pathname +
            (params.toString() ? '?' + params.toString() : '') +
            window.location.hash;
          window.history.replaceState({}, '', clean);
        }
      } catch (e) { /* no-op */ }
    }

    form.addEventListener('submit', onSubmit);

    // ---- attachments: attach button opens the OS file picker ----
    if (attachBtn && fileInput) {
      attachBtn.addEventListener('click', () => {
        if (attachments.length >= MAX_ATTACHMENTS) {
          alert('Max ' + MAX_ATTACHMENTS + ' photos per message.');
          return;
        }
        fileInput.click();
      });
      fileInput.addEventListener('change', (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // reset so re-picking the same file works
        files.forEach(queueAttachment);
      });
    }

    // ---- attachments: drag-drop anywhere on the chat panel ----
    if (dropzone && panel) {
      let dragDepth = 0;
      const isImageDrag = (dt) => {
        if (!dt) return false;
        if (dt.types && dt.types.includes && dt.types.includes('Files')) return true;
        for (let i = 0; i < (dt.types?.length || 0); i++) if (dt.types[i] === 'Files') return true;
        return false;
      };
      panel.addEventListener('dragenter', (e) => {
        if (!isImageDrag(e.dataTransfer)) return;
        e.preventDefault();
        dragDepth++;
        dropzone.setAttribute('data-visible', 'true');
      });
      panel.addEventListener('dragover', (e) => {
        if (!isImageDrag(e.dataTransfer)) return;
        e.preventDefault();
      });
      panel.addEventListener('dragleave', (e) => {
        if (!isImageDrag(e.dataTransfer)) return;
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) dropzone.removeAttribute('data-visible');
      });
      panel.addEventListener('drop', (e) => {
        if (!isImageDrag(e.dataTransfer)) return;
        e.preventDefault();
        dragDepth = 0;
        dropzone.removeAttribute('data-visible');
        const files = Array.from(e.dataTransfer.files || []);
        files.forEach(queueAttachment);
      });
    }

    // ---- attachments: upload flow via mel-worker /api/review/upload-* ----

    function queueAttachment(file) {
      if (!file) return;
      if (attachments.length >= MAX_ATTACHMENTS) {
        alert('Max ' + MAX_ATTACHMENTS + ' photos per message.');
        return;
      }
      if (!ALLOWED_MIME.test(file.type || '')) {
        alert('Only JPEG, PNG, or WebP images are supported.');
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        alert('Photo is too large. Max 5MB per photo.');
        return;
      }
      const entry = {
        id: 'att-' + (++attachSeq),
        name: file.name || 'photo.jpg',
        mime: file.type || 'image/jpeg',
        size: file.size,
        thumbUrl: URL.createObjectURL(file),
        status: 'uploading',
      };
      attachments.push(entry);
      renderAttachments();
      uploadAttachment(entry, file).catch((err) => {
        entry.status = 'error';
        entry.error = (err && err.message) || String(err);
        renderAttachments();
      });
    }

    async function uploadAttachment(entry, file) {
      // Step 1: request a staged upload target from mel-worker.
      const startRes = await fetch(WORKER_URL + '/api/review/upload-start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ filename: entry.name, mime_type: entry.mime, size: entry.size }),
      });
      const startData = await startRes.json();
      if (!startRes.ok || !startData.ok) {
        throw new Error(startData.error || 'upload-start failed');
      }

      // Step 2: multipart POST direct to Shopify staged target.
      const fd = new FormData();
      (startData.parameters || []).forEach((p) => fd.append(p.name, p.value));
      fd.append('file', file, entry.name);
      const putRes = await fetch(startData.url, { method: 'POST', body: fd });
      if (!putRes.ok) {
        throw new Error('upload to storage failed (HTTP ' + putRes.status + ')');
      }

      // Step 3: finalize — mel-worker calls fileCreate + polls for CDN URL.
      const finRes = await fetch(WORKER_URL + '/api/review/upload-finalize', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ resource_url: startData.resource_url, alt: 'Customer chat photo' }),
      });
      const finData = await finRes.json();
      if (!finRes.ok || !finData.ok || !finData.url) {
        throw new Error(finData.error || 'finalize failed');
      }

      entry.status = 'ready';
      entry.url = finData.url;
      renderAttachments();
    }

    function removeAttachment(id) {
      const idx = attachments.findIndex((a) => a.id === id);
      if (idx < 0) return;
      const [gone] = attachments.splice(idx, 1);
      try { if (gone.thumbUrl) URL.revokeObjectURL(gone.thumbUrl); } catch (e) {}
      renderAttachments();
    }

    function renderAttachments() {
      if (!attachStrip) return;
      if (attachments.length === 0) {
        attachStrip.hidden = true;
        attachStrip.innerHTML = '';
        return;
      }
      attachStrip.hidden = false;
      attachStrip.innerHTML = '';
      attachments.forEach((a) => {
        const tile = document.createElement('div');
        tile.className = 'altera-mel-att' + (a.status === 'uploading' ? ' is-uploading' : '') + (a.status === 'error' ? ' is-error' : '');
        const img = document.createElement('img');
        img.className = 'altera-mel-att-thumb';
        img.src = a.thumbUrl;
        img.alt = a.name;
        tile.appendChild(img);
        if (a.status === 'uploading') {
          const spin = document.createElement('div');
          spin.className = 'altera-mel-att-spin';
          tile.appendChild(spin);
        }
        if (a.status === 'error') {
          const err = document.createElement('div');
          err.className = 'altera-mel-att-err';
          err.textContent = 'Upload failed';
          err.title = a.error || 'Unknown error';
          tile.appendChild(err);
        }
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'altera-mel-att-remove';
        rm.setAttribute('aria-label', 'Remove attachment');
        rm.textContent = '×';
        rm.addEventListener('click', () => removeAttachment(a.id));
        tile.appendChild(rm);
        attachStrip.appendChild(tile);
      });
    }

    // ---- handlers ----
    function greetingText() {
      if (IS_WHOLESALE) {
        return CUSTOMER_NAME
          ? `Hi ${CUSTOMER_NAME} — ${WHOLESALE_GREETING.replace(/^welcome[!,\s]*/i, '')}`
          : WHOLESALE_GREETING;
      }
      return CUSTOMER_NAME
        ? `Hi ${CUSTOMER_NAME} — ${GREETING.replace(/^hi[, ]*/i, '')}`
        : GREETING;
    }

    function openPanel(focusInput) {
      panel.hidden = false;
      if (bubble) bubble.setAttribute('aria-expanded', 'true');

      // Tag this Clarity session as Mel-opened (separate from mel_engaged
      // which only fires on message send). Mirror to GA4 as a custom event.
      // Both silent-fail if their tag isn't loaded.
      try {
        if (typeof window.clarity === 'function') {
          window.clarity('set', 'mel_opened', 'true');
        }
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'mel_opened', { event_category: 'mel' });
        }
      } catch (e) {}

      if (messages.length === 0) {
        appendMessage('assistant', greetingText(), { persist: true });
        renderStarterChips();
      }

      if (focusInput) {
        setTimeout(() => input.focus(), 50);
      }
      scrollToBottom();
    }

    function closePanel() {
      panel.hidden = true;
      if (bubble) bubble.setAttribute('aria-expanded', 'false');
    }

    function onKeyDown(e) {
      if (e.key === 'Escape' && !panel.hidden) {
        closePanel();
        if (bubble) bubble.focus();
      }
    }

    async function onSubmit(e) {
      e.preventDefault();
      if (inFlight) return;

      const text = input.value.trim();
      // Reject sends with attachments still uploading — the message won't
      // include the URLs otherwise and Mel would have nothing to submit.
      const uploading = attachments.filter((a) => a.status === 'uploading').length;
      if (uploading > 0) {
        alert('Still uploading ' + uploading + ' photo' + (uploading > 1 ? 's' : '') + ' — one moment.');
        return;
      }
      const ready = attachments.filter((a) => a.status === 'ready');
      // Allow send if the customer typed text OR attached at least one photo.
      if (!text && ready.length === 0) return;

      const outgoingAttachments = ready.map((a) => ({ url: a.url, name: a.name, mime: a.mime }));
      // Clear the queue immediately so a double-click doesn't re-send.
      attachments.forEach((a) => { try { URL.revokeObjectURL(a.thumbUrl); } catch (e) {} });
      attachments = [];
      renderAttachments();

      // Starter-prompt chips are one-shot: once the customer sends anything,
      // they collapse away.
      removeStarterChips();

      appendMessage('user', text, { persist: true, attachments: outgoingAttachments });
      input.value = '';

      // Tag this Clarity session as Mel-engaged (only fires once per session
      // since clarity dedups identical set() calls). Mirror to GA4 as a
      // custom event. Also set a sessionStorage flag so the cart-detection
      // script in theme.liquid can fire mel_post_convert when this user
      // adds to cart after chatting.
      try {
        if (typeof window.clarity === 'function') {
          window.clarity('set', 'mel_engaged', 'true');
        }
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'mel_engaged', { event_category: 'mel' });
        }
        sessionStorage.setItem('altera-mel-engaged', '1');
      } catch (e) {}

      inFlight = true;
      setSending(true);

      try {
        const reply = await callMel(messages);
        appendMessage('assistant', reply, { persist: true });
      } catch (err) {
        console.error('[mel] chat error', err);
        appendMessage(
          'assistant',
          "Sorry — I'm having trouble reaching the server. Try again in a moment, or email support@alteraalpaca.com.",
          { persist: false, isError: true }
        );
      } finally {
        inFlight = false;
        setSending(false);
        input.focus();
      }
    }

    async function callMel(history) {
      const payload = {
        messages: history.map((m) => {
          const out = { role: m.role, content: m.content };
          if (Array.isArray(m.attachments) && m.attachments.length) {
            out.attachments = m.attachments
              .map((a) => (a && a.url ? { url: a.url, mime: a.mime || '', name: a.name || '' } : null))
              .filter(Boolean);
          }
          return out;
        }),
        sessionId: SESSION_ID,
        pageUrl: (window.location && window.location.href) || '',
      };
      if (CUSTOMER_ID) payload.customerId = CUSTOMER_ID;
      if (CUSTOMER_NAME) payload.customerName = CUSTOMER_NAME;
      if (CUSTOMER_EMAIL) payload.customerEmail = CUSTOMER_EMAIL;
      if (IS_WHOLESALE) payload.customerIsWholesale = true;

      const res = await fetch(`${WORKER_URL}/api/chat`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`Worker ${res.status}: ${body.slice(0, 200)}`);
      }

      const data = await res.json();
      return data.reply || '';
    }

    // ---- UI ----

    /* Turn Markdown-style [text](https://url) and bare https:// URLs in
       Mel's message text into real <a> tags. Everything non-link is escaped
       via createTextNode so this stays XSS-safe. Called by renderBubbleContent
       and renderUserContent for consistency. */
    function appendLinkifiedText(container, text) {
      if (!text) return;
      const src = String(text);
      // Combined regex: markdown [label](url) first, then a bare https URL.
      // Groups: 1 = markdown label, 2 = markdown url, 3 = bare url
      const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)<>\]]+[^\s)<>\].,;:!?])/g;
      let lastIdx = 0;
      let m;
      while ((m = re.exec(src)) !== null) {
        if (m.index > lastIdx) {
          container.appendChild(document.createTextNode(src.slice(lastIdx, m.index)));
        }
        const label = m[1] || m[3];
        const url = m[2] || m[3];
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.className = 'altera-mel-link';
        a.textContent = label;
        container.appendChild(a);
        lastIdx = re.lastIndex;
      }
      if (lastIdx < src.length) {
        container.appendChild(document.createTextNode(src.slice(lastIdx)));
      }
    }

    /* Strip the hidden <order_pad>{...}</order_pad> tag from Mel's response,
       render the prose to the bubble, and append an "Add to Order Pad"
       button when the tag is present and parseable. */
    function renderBubbleContent(bubbleEl, content) {
      const match = String(content || '').match(/<order_pad>([\s\S]*?)<\/order_pad>/i);
      if (!match) {
        appendLinkifiedText(bubbleEl, content);
        return;
      }
      const cleanText = content.replace(/<order_pad>[\s\S]*?<\/order_pad>/i, '').trim();
      appendLinkifiedText(bubbleEl, cleanText);

      let items = null;
      try {
        const parsed = JSON.parse(match[1].trim());
        items = Array.isArray(parsed.items) ? parsed.items : null;
      } catch (e) { items = null; }

      const valid = (items || []).filter(it => {
        const vid = String(it && it.variant_id || '').replace(/[^\d]/g, '');
        const qty = parseInt(it && it.qty, 10);
        return vid && qty > 0;
      });
      if (!valid.length) return;

      const totalPairs = valid.reduce((s, it) => s + (parseInt(it.qty, 10) || 0), 0);

      const cta = document.createElement('button');
      cta.type = 'button';
      cta.className = 'altera-mel-add-to-pad';
      cta.textContent = `Add to Order Pad — ${valid.length} sku${valid.length === 1 ? '' : 's'} · ${totalPairs} pairs →`;
      cta.addEventListener('click', () => addItemsToOrderPad(valid, cta));
      bubbleEl.appendChild(cta);
    }

    /* Merge items into the Order Pad's localStorage map (keyed by variant_id)
       and either reload the catalog page (so qty inputs re-sync from storage)
       or navigate to it when the dealer is somewhere else. */
    function addItemsToOrderPad(items, btnEl) {
      const KEY = 'altera-wholesale-order-v1';
      let map = {};
      try { map = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { map = {}; }
      items.forEach(it => {
        const vid = String(it.variant_id).replace(/[^\d]/g, '');
        const qty = parseInt(it.qty, 10) || 0;
        if (!vid || qty <= 0) return;
        map[vid] = (parseInt(map[vid], 10) || 0) + qty;
      });
      try { localStorage.setItem(KEY, JSON.stringify(map)); } catch (e) {}

      // Hint to the catalog page to enter order mode automatically on next paint
      try { sessionStorage.setItem('altera-w-open-order-mode', '1'); } catch (e) {}

      if (btnEl) {
        btnEl.disabled = true;
        btnEl.textContent = 'Added — loading catalog…';
      }

      const onCatalog = /\/pages\/wholesale-catalog\b/.test(window.location.pathname);
      setTimeout(() => {
        if (onCatalog) window.location.reload();
        else window.location.href = '/pages/wholesale-catalog';
      }, 350);
    }

    function appendMessage(role, content, opts) {
      const o = opts || {};
      const msg = { role, content, ts: Date.now() };
      if (Array.isArray(o.attachments) && o.attachments.length) msg.attachments = o.attachments;

      if (o.persist !== false) {
        messages.push(msg);
        saveConversation();
      }

      const row = document.createElement('div');
      row.className = `altera-mel-msg altera-mel-msg-${role}` + (o.isError ? ' altera-mel-msg-error' : '');

      const bubbleEl = document.createElement('div');
      bubbleEl.className = 'altera-mel-msg-bubble';
      if (role === 'assistant') {
        renderBubbleContent(bubbleEl, content);
      } else {
        renderUserContent(bubbleEl, content, msg.attachments);
      }
      row.appendChild(bubbleEl);

      messagesEl.appendChild(row);
      scrollToBottom();
    }

    function renderAllMessages() {
      messagesEl.innerHTML = '';
      messages.forEach((m) => {
        const row = document.createElement('div');
        row.className = `altera-mel-msg altera-mel-msg-${m.role}`;
        const bubbleEl = document.createElement('div');
        bubbleEl.className = 'altera-mel-msg-bubble';
        if (m.role === 'assistant') {
          renderBubbleContent(bubbleEl, m.content);
        } else {
          renderUserContent(bubbleEl, m.content, m.attachments);
        }
        row.appendChild(bubbleEl);
        messagesEl.appendChild(row);
      });
    }

    /* User bubble: optional text plus a grid of thumbnail images for any
       attachments that were uploaded with the message. */
    function renderUserContent(bubbleEl, content, atts) {
      if (content) {
        const p = document.createElement('div');
        p.className = 'altera-mel-msg-text';
        p.textContent = content;
        bubbleEl.appendChild(p);
      }
      const list = Array.isArray(atts) ? atts.filter((a) => a && a.url) : [];
      if (list.length === 0) return;
      const grid = document.createElement('div');
      grid.className = 'altera-mel-msg-atts';
      list.forEach((a) => {
        const link = document.createElement('a');
        link.href = a.url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.className = 'altera-mel-msg-att';
        const img = document.createElement('img');
        img.src = a.url;
        img.alt = a.name || 'Attached photo';
        img.loading = 'lazy';
        link.appendChild(img);
        grid.appendChild(link);
      });
      bubbleEl.appendChild(grid);
    }

    function setSending(sending) {
      sendBtn.disabled = sending;
      input.disabled = sending;
      typingEl.hidden = !sending;
      typingEl.setAttribute('aria-hidden', sending ? 'false' : 'true');
      if (sending) scrollToBottom();
    }

    function scrollToBottom() {
      requestAnimationFrame(() => {
        messagesEl.scrollTop = messagesEl.scrollHeight;
      });
    }

    /* Render a single-use starter-prompt row below the greeting bubble.
       Tapping a chip drops the text into the input and immediately submits.
       Chips vanish the moment the customer sends any message (whether from
       a chip or free-typed). */
    function renderStarterChips() {
      // Guard: don't render twice
      if (messagesEl.querySelector('.altera-mel-chips')) return;
      const chips = starterPromptsForCustomer();
      if (!chips || chips.length === 0) return;
      const row = document.createElement('div');
      row.className = 'altera-mel-chips';
      row.setAttribute('data-mel-chips', '');
      chips.forEach((text) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'altera-mel-chip';
        b.textContent = text;
        b.addEventListener('click', () => {
          input.value = text;
          removeStarterChips();
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
        row.appendChild(b);
      });
      messagesEl.appendChild(row);
      scrollToBottom();
    }
    function removeStarterChips() {
      const row = messagesEl.querySelector('[data-mel-chips]');
      if (row) row.remove();
    }
  }

  // ---- persistence (module-shared so multiple Mel instances share history) ----
  function loadConversation() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function saveConversation() {
    try {
      const trimmed = messages.slice(-20);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch {
      /* sessionStorage quota or disabled — silently degrade */
    }
  }
})();
